import { Checkbox, ListItemButton, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { SECURITY_ACTIONS } from '../common/auth/Security';
import type { SecuredActionType } from '../common/components/ActionType';
import ResponsiveActions from '../common/components/ResponsiveActions';
import I18n from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { C, H } from '../common/Layout';
import { useStyling } from '../common/Theme';
import { AttributeGroupProps } from './AttributeGroupPreview';
import { AttributePreview } from './AttributePreview';
import { getPreviewComponent, getTileStyle } from './FieldSelector';

export default function AttributeGroupPreviewSection({
    isHover,
    attributes,
    enableEdit,
    onEdit,
    group,
    enableDelete,
    onDelete,
    isVerticalPreview,
    onClickAttribute,
    isSelected,
    isInModal,
    disableFlexGrow = false,
    onToggleGroupSelection,
    entityName
}: AttributeGroupProps) {
    const { theme } = useStyling();
    const [activatedId, setActivatedId] = useState<string>(undefined);

    const allSelected = isSelected && attributes?.length > 0 && attributes.every((attr) => isSelected(attr));

    const someSelected =
        isSelected && attributes?.length > 0 && attributes.some((attr) => isSelected(attr)) && !allSelected;

    const handleGroupCheckboxChange = () => {
        if (onToggleGroupSelection) {
            onToggleGroupSelection(attributes, !allSelected);
        }
    };

    const editSecuredActions = useMemo<SecuredActionType[]>(
        () => [
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName,
                label: <I18n token="form.title.edit" />,
                icon: Icons.Edit,
                onClick: () => onEdit?.(group)
            }
        ],
        [entityName, group, onEdit]
    );

    const makeDeleteSecuredActions = useCallback(
        (attributeId: string): SecuredActionType[] => [
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName,
                label: <I18n token="fieldDefinition.attribute.delete" />,
                icon: Icons.Delete,
                onClick: () => onDelete?.(attributeId)
            }
        ],
        [entityName, onDelete]
    );

    return (
        <>
            <H sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <H sx={{ alignItems: 'center', gap: 1 }}>
                    {isInModal && onToggleGroupSelection && attributes?.length > 0 && (
                        <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={handleGroupCheckboxChange}
                            slotProps={{ input: { 'aria-label': `Select all ${group} attributes` } }}
                            data-testid={`group-select-checkbox-${group}`}
                            sx={{ p: 0 }}
                        />
                    )}
                    <Typography fontSize={'medium'}>{group}</Typography>
                </H>
                {enableEdit && Boolean(entityName) && (
                    <ResponsiveActions
                        isActivated={isHover}
                        securedActions={editSecuredActions}
                    />
                )}
            </H>
            <H sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <C
                    sx={{
                        display: 'flex',
                        flexDirection: isVerticalPreview ? 'column' : 'row',
                        alignContent: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 4,
                        rowGap: 2
                    }}
                >
                    {attributes &&
                        attributes.map((attribute, index) => {
                            const Preview = getPreviewComponent?.(undefined, attribute);
                            const selected = isSelected?.(attribute);
                            const isLast = index === attributes.length - 1;

                            return (
                                <H
                                    sx={{
                                        width: 'fit-content',
                                        alignItems: { xs: 'flex-end', sm: 'flex-start' },
                                        gap: 1,
                                        flexGrow: disableFlexGrow ? undefined : 1
                                    }}
                                    onMouseEnter={() => setActivatedId(attribute.id)}
                                    onMouseLeave={() => setActivatedId(undefined)}
                                    key={index}
                                >
                                    {isInModal ? (
                                        <ListItemButton
                                            key={index}
                                            sx={{
                                                ...getTileStyle(theme, selected, isLast, 1),
                                                minHeight: '78px',
                                                height: 'auto'
                                            }}
                                            onClick={() => onClickAttribute?.(attribute)}
                                        >
                                            <Preview field={attribute} />
                                        </ListItemButton>
                                    ) : (
                                        <AttributePreview attribute={attribute} key={attribute.name + index} />
                                    )}

                                    {enableDelete && Boolean(entityName) && (
                                        <H sx={{ width: '30px' }}>
                                            <ResponsiveActions
                                                isActivated={activatedId === attribute.id}
                                                securedActions={makeDeleteSecuredActions(attribute.id)}
                                            />
                                        </H>
                                    )}
                                </H>
                            );
                        })}
                </C>
            </H>
        </>
    );
}
